import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
  Skeleton
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Webhook as WebhookIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User, Tenant } from '../types/dashboard';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useDesktopTier } from '../hooks/useDesktopTier';
import LimitedModeView from './limited-mode/LimitedModeView';

interface LandingPageProps {
  user: User;
  tenant: Tenant;
}

const LandingPage: React.FC<LandingPageProps> = ({ user, tenant }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDesktopTier, loading: tierLoading } = useDesktopTier();
  const [systemStats, setSystemStats] = useState({
    webhookCount: 0,
    alertCount: 0,
    uptime: 0 as number,
    responseTime: 0 as number
  });
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);

  // Load system statistics (must be before any conditional returns)
  useEffect(() => {
    // Only load stats if not in desktop tier mode
    if (isDesktopTier || tierLoading) {
      return;
    }

    let cancelled = false;

    const loadSystemStats = async () => {
      const isInitialLoad = !hasLoadedRef.current;
      try {
        if (isInitialLoad) setLoading(true);

        const [statusResponse, configsResponse] = await Promise.all([
          apiService.getSystemStatus().catch(() => ({ success: false as const, data: null })),
          apiService.getAlertConfigurations(token || undefined).catch(() => ({
            success: false as const,
            data: [] as const,
          })),
        ]);

        if (cancelled) return;

        if (statusResponse.success && statusResponse.data) {
          const data = statusResponse.data;
          const responseMs =
            data.responseTimeMs ??
            (data.executionTime && data.executionTime > 0
              ? Math.round(data.executionTime * 1000)
              : 0);
          setSystemStats((prev) => ({
            ...prev,
            uptime: data.uptime ?? 0,
            responseTime: responseMs,
          }));
        }

        if (configsResponse.success && Array.isArray(configsResponse.data)) {
          const configs = configsResponse.data;
          const activeAlerts = configs.filter((c) => c.is_active).length;
          const withDestination = configs.filter(
            (c) => (c.webhook_url ?? c.webhookUrl ?? '').trim().length > 0
          ).length;
          setSystemStats((prev) => ({
            ...prev,
            alertCount: activeAlerts,
            webhookCount: withDestination,
          }));
        }
      } catch (error) {
        console.error('Error loading system stats:', error);
      } finally {
        hasLoadedRef.current = true;
        if (!cancelled && isInitialLoad) setLoading(false);
      }
    };

    loadSystemStats();
    const intervalId = window.setInterval(loadSystemStats, 30_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isDesktopTier, tierLoading, token]);

  // Show limited mode view for desktop tier users
  if (tierLoading) {
    return <div>Loading...</div>;
  }

  if (isDesktopTier) {
    return <LimitedModeView user={user} tenant={tenant} />;
  }

  const quickActions = [
    {
      title: 'Dashboard',
      description: 'View system status and performance metrics',
      icon: <DashboardIcon sx={{ fontSize: 40 }} />,
      path: '/dashboard',
      color: 'primary'
    },
    {
      title: 'Alerts',
      description: 'Configure trading alerts and notifications',
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      path: '/alerts',
      color: 'secondary'
    },
    {
      title: 'Email Management',
      description: 'Manage email addresses and processing',
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      path: '/email',
      color: 'success'
    },
    {
      title: 'Webhook Activity',
      description: 'Review recent incoming webhook triggers and errors',
      icon: <WebhookIcon sx={{ fontSize: 40 }} />,
      path: '/webhook-activity',
      color: 'secondary'
    },
    {
      title: 'Settings',
      description: 'Manage subscription and preferences',
      icon: <SettingsIcon sx={{ fontSize: 40 }} />,
      path: '/settings',
      color: 'info'
    }
  ];

  // Add admin action only for admin users
  if (user.role === 'admin') {
    quickActions.push({
      title: 'Admin',
      description: 'Administrative functions',
      icon: <AdminIcon sx={{ fontSize: 40 }} />,
      path: '/admin',
      color: 'error'
    });
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Section */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Welcome to Branch Router
        </Typography>
        <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
          Hello, {user.name}! You're logged in as {user.email}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Tenant: ${tenant.name}`} 
            color="secondary" 
            variant="outlined" 
            sx={{ color: 'white', borderColor: 'white' }}
          />
          <Chip 
            label={`Role: ${user.role}`} 
            color="secondary" 
            variant="outlined" 
            sx={{ color: 'white', borderColor: 'white' }}
          />
        </Box>
      </Paper>

      {/* Quick Actions Grid */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Quick Actions
      </Typography>
      
      <Grid container spacing={3}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                <Box sx={{ color: `${action.color}.main`, mb: 2 }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" component="h3" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {action.description}
                </Typography>
                <Button
                  variant="contained"
                  color={action.color as any}
                  fullWidth
                  onClick={() => navigate(action.path)}
                  sx={{ mt: 'auto' }}
                >
                  Go to {action.title}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* System Status Summary */}
      <Paper elevation={1} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" component="h3" gutterBottom>
          System Status
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="success.main">
                  {systemStats.uptime > 0 ? `${systemStats.uptime}%` : '—'}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Delivery success (7 days)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="primary.main">
                  {systemStats.responseTime > 0 ? `${systemStats.responseTime}ms` : '—'}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Avg. webhook response
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="info.main">
                  {systemStats.alertCount}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Active Alerts
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              {loading ? (
                <Skeleton variant="text" width={60} height={40} />
              ) : (
                <Typography variant="h4" color="warning.main">
                  {systemStats.webhookCount}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Webhooks
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default LandingPage;

